// Un intento de MetaMap por flujo del paso 02. La etapa y el QR salen de `progress[flow]` del poll;
// aquí solo vive lo que no viaja: el SDK abierto, la confirmación y los avisos de la tablet.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { KycAttemptStageEnum } from '@fiado/type-kit/bin/retailWizard/enums/KycAttemptStageEnum';
import { wizardApi } from '@/api/services/retail-wizard';
import { ApiError } from '@/api/errors';
import {
    KYC_ALREADY_VERIFIED,
    clientLeftSdk,
    clientLooksStuck,
    effectiveStage,
    linkMissing,
    qrVisible,
    stuckClockStart,
    switchNeedsConfirmation,
} from '../metamapAttempt';
import type {
    KycAttemptProgress,
    KycModality,
    KycPrepareResponse,
    KycState,
    MetamapFlow,
    ProofOfAddressPrepareResponse,
} from '../types';
import { useElapsedSince } from './useElapsedSince';
import { WIZARD_KEYS } from './useWizard';

/** Config del `<metamap-button>` que entrega el prepare; se pasa tal cual. */
export interface SdkConfig {
    clientId: string;
    flowId: string;
    metadata: string;
}

/** SDK abierto y el intento del prepare al que pertenece; `attemptId` null si el prepare no lo trajo. */
export interface SdkLaunch {
    config: SdkConfig;
    attemptId: string | null;
}

export interface SdkCallbacks {
    onStarted: () => void;
    onFinished: () => void;
    onExited: () => void;
}

/** Lo que el hook necesita del poll de `kyc/status` (ver `useKycAttemptPoll`). */
export interface AttemptPoll {
    data: KycState | undefined;
    refetch: () => Promise<{ data?: KycState }>;
    attemptTimedOut: boolean;
    restartAttemptClock: () => void;
    /** El poll sigue preguntando hasta traer este intento del prepare. */
    awaitAttempt: (flow: MetamapFlow, attemptId: string) => void;
}

/** Intento que abrió el último prepare de esta pantalla. */
interface BoundAttempt {
    attemptId: string;
    modality: KycModality;
    /** URL que devolvió el prepare; puede ser la larga, que el silo no persiste. */
    link: string | null;
    preparedAt: string;
}

export interface MetamapAttemptOptions {
    poll: AttemptPoll;
    /** Nada que capturar: identidad vigente, domicilio registrado o 409 KYC_ALREADY_VERIFIED. */
    onAlreadyDone: () => void | Promise<void>;
}

export interface MetamapAttempt {
    flow: MetamapFlow;
    progress: KycAttemptProgress | null;
    /** Etapa vigente, con el `onStarted` de la tablet adelantando STARTED. `null` = sin intento. */
    stage: KycAttemptStageEnum | null;
    /** Liga a pintar ahora; `null` cuando no toca mostrar el QR. */
    qrUrl: string | null;
    /** Hay QR pero se ocultó porque el cliente ya empezó. */
    qrHidden: boolean;
    linkUnavailable: boolean;
    configUnavailable: boolean;
    sdk: SdkLaunch | null;
    sdkCallbacks: SdkCallbacks;
    clientLeft: boolean;
    stuck: boolean;
    timedOut: boolean;
    isPending: boolean;
    /** Botón que disparó el prepare en vuelo: solo ese muestra carga. */
    busyAction: LaunchAction | null;
    /** Error del último prepare; el 409 KYC_ALREADY_VERIFIED no cuenta porque ya se resolvió. */
    error: unknown;
    pendingSwitch: KycModality | null;
    /** `action` nombra el botón que lanza; por omisión, la modalidad. */
    launch: (modality: KycModality, action?: LaunchAction) => void;
    confirmSwitch: () => void;
    cancelSwitch: () => void;
    revealQr: () => void;
    reopenSdk: () => void;
    closeSdk: () => void;
    checkNow: () => Promise<void>;
}

/** Botones que lanzan un prepare; `REOPEN` es «Empezar verificación» en la tablet. */
export const LAUNCH_ACTION = {
    SWITCH: 'switch',
    RELAUNCH: 'relaunch',
    QR: 'qr',
    TABLET: 'tablet',
    REOPEN: 'reopen',
} as const;

/** Qué botón lanzó el prepare; sin botón explícito, la modalidad. */
export type LaunchAction = (typeof LAUNCH_ACTION)[keyof typeof LAUNCH_ACTION] | KycModality;

const NO_CALLBACKS: SdkCallbacks = {
    onStarted: () => undefined,
    onFinished: () => undefined,
    onExited: () => undefined,
};

type PrepareResponse = KycPrepareResponse | ProofOfAddressPrepareResponse;

function prepareFor(
    sessionId: string,
    flow: MetamapFlow,
    modality: KycModality,
): Promise<PrepareResponse> {
    return flow === 'proofOfAddress'
        ? wizardApi.proofOfAddressPrepare(sessionId, modality)
        : wizardApi.kycPrepare(sessionId, modality);
}

function nothingToCapture(res: PrepareResponse): boolean {
    return (
        ('alreadyVerified' in res && res.alreadyVerified === true) ||
        ('alreadyHasAddress' in res && res.alreadyHasAddress === true)
    );
}

function sdkConfigOf(res: PrepareResponse): SdkConfig | null {
    if (!res.clientId || !res.flowId || !res.metadata) return null;
    return { clientId: res.clientId, flowId: res.flowId, metadata: res.metadata };
}

export function isAlreadyVerified(error: unknown): boolean {
    return error instanceof ApiError && error.code === KYC_ALREADY_VERIFIED;
}

/** El intento del prepare manda; `progress[flow]` cuenta solo cuando ya trae ese mismo `attemptId`. */
function boundProgress(
    polled: KycAttemptProgress | null,
    bound: BoundAttempt | null,
): KycAttemptProgress | null {
    if (bound === null) return polled;
    if (polled?.attemptId === bound.attemptId) {
        // Sin link persistido (fallback de URL larga) el QR sigue con la del prepare.
        return polled.link === null && bound.link !== null
            ? { ...polled, link: bound.link }
            : polled;
    }
    return {
        attemptId: bound.attemptId,
        modality: bound.modality,
        link: bound.link,
        stage: KycAttemptStageEnum.AWAITING_START,
        stageAt: bound.preparedAt,
        attemptStartedAt: bound.preparedAt,
    };
}

export function useMetamapAttempt(
    sessionId: string,
    flow: MetamapFlow,
    { poll, onAlreadyDone }: MetamapAttemptOptions,
): MetamapAttempt {
    const qc = useQueryClient();
    const prepare = useMutation({
        mutationFn: (modality: KycModality) => prepareFor(sessionId, flow, modality),
    });
    const [sdk, setSdk] = useState<SdkLaunch | null>(null);
    const [lastSdk, setLastSdk] = useState<SdkLaunch | null>(null);
    const sdkRef = useRef<SdkLaunch | null>(null);
    const finishedRef = useRef<SdkLaunch | null>(null);
    const [startedFor, setStartedFor] = useState<string | null>(null);
    const [exitedFor, setExitedFor] = useState<string | null>(null);
    const [revealedFor, setRevealedFor] = useState<string | null>(null);
    const [linkUnavailable, setLinkUnavailable] = useState(false);
    const [configUnavailable, setConfigUnavailable] = useState(false);
    const [pendingSwitch, setPendingSwitch] = useState<KycModality | null>(null);
    const [switchAction, setSwitchAction] = useState<LaunchAction | null>(null);
    const [launchedBy, setLaunchedBy] = useState<LaunchAction | null>(null);
    const [bound, setBound] = useState<BoundAttempt | null>(null);

    useEffect(() => {
        sdkRef.current = sdk;
    }, [sdk]);

    const progress = boundProgress(poll.data?.progress?.[flow] ?? null, bound);
    const stage = effectiveStage(progress, startedFor);
    // El aviso cuenta desde el `stageAt` de STARTED del silo: recargar no lo reinicia.
    const msInStarted = useElapsedSince(stuckClockStart(progress));

    // La sesión se relee al llegar el veredicto del intento, no en cada vuelta del poll.
    const resultFor = progress?.stage === KycAttemptStageEnum.RESULT ? progress.attemptId : null;
    useEffect(() => {
        if (resultFor === null) return;
        void qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
        if (flow === 'proofOfAddress') {
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.profile(sessionId) });
        }
    }, [resultFor, flow, qc, sessionId]);

    const runLaunch = (modality: KycModality, action: LaunchAction = modality): void => {
        setLaunchedBy(action);
        setLinkUnavailable(false);
        setConfigUnavailable(false);
        void prepare
            .mutateAsync(modality)
            .then(
                async (res) => {
                    // El prepare mueve la sesión en el silo, también cuando no hay nada que capturar.
                    void qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
                    // `alreadyVerified` / `alreadyHasAddress` no abren intento ni traen `attemptId`.
                    if (nothingToCapture(res)) {
                        await onAlreadyDone();
                        return;
                    }
                    // El QR viejo se suelta aquí: SDK, callbacks y QR se ligan al intento del prepare.
                    const attemptId = res.attemptId ?? null;
                    const link = modality === 'CUSTOMER_LINK' ? (res.shortUrl ?? null) : null;
                    setBound(
                        attemptId === null
                            ? null
                            : { attemptId, modality, link, preparedAt: new Date().toISOString() },
                    );
                    if (attemptId !== null) poll.awaitAttempt(flow, attemptId);
                    setStartedFor(null);
                    setExitedFor(null);
                    setRevealedFor(null);
                    poll.restartAttemptClock();
                    if (modality === 'CUSTOMER_LINK') {
                        setSdk(null);
                        setLastSdk(null);
                        setLinkUnavailable(link === null);
                    } else {
                        const config = sdkConfigOf(res);
                        setConfigUnavailable(config === null);
                        const launched = config ? { config, attemptId } : null;
                        setSdk(launched);
                        setLastSdk(launched);
                    }
                    await poll.refetch();
                },
                async (error: unknown) => {
                    if (!isAlreadyVerified(error)) return;
                    // Otra pestaña ya cerró la verificación: se relee y se avanza, sin relanzar.
                    await qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
                    await poll.refetch();
                    await onAlreadyDone();
                },
            )
            .catch(() => undefined);
    };

    const launch = (modality: KycModality, action: LaunchAction = modality): void => {
        if (switchNeedsConfirmation(stage)) {
            setPendingSwitch(modality);
            setSwitchAction(action);
            return;
        }
        runLaunch(modality, action);
    };

    const confirmSwitch = (): void => {
        if (pendingSwitch === null) return;
        const modality = pendingSwitch;
        setPendingSwitch(null);
        runLaunch(modality, switchAction ?? modality);
    };

    // Los eventos `metamap:*` son de `window`: solo cuenta el SDK del intento vigente.
    const sdkCallbacks = useMemo<SdkCallbacks>(() => {
        if (sdk === null) return NO_CALLBACKS;
        const launched = sdk;
        return {
            onStarted: () => {
                if (sdkRef.current === launched && launched.attemptId)
                    setStartedFor(launched.attemptId);
            },
            onFinished: () => {
                if (sdkRef.current !== launched) return;
                finishedRef.current = launched;
                setSdk(null);
            },
            onExited: () => {
                if (sdkRef.current !== launched) return;
                if (finishedRef.current !== launched && launched.attemptId) {
                    setExitedFor(launched.attemptId);
                }
                setSdk(null);
            },
        };
    }, [sdk]);

    const reopenSdk = (): void => {
        if (lastSdk !== null && lastSdk.attemptId === (progress?.attemptId ?? null)) {
            setExitedFor(null);
            setSdk(lastSdk);
            return;
        }
        runLaunch('STORE_DEVICE', LAUNCH_ACTION.REOPEN);
    };

    const showQr = qrVisible(progress, revealedFor !== null && revealedFor === progress?.attemptId);

    return {
        flow,
        progress,
        stage,
        qrUrl: showQr && progress ? progress.link : null,
        qrHidden:
            !showQr &&
            progress?.modality === 'CUSTOMER_LINK' &&
            progress.link !== null &&
            stage === KycAttemptStageEnum.STARTED,
        linkUnavailable: linkUnavailable || linkMissing(progress),
        configUnavailable,
        sdk,
        sdkCallbacks,
        clientLeft: clientLeftSdk(exitedFor, progress, stage),
        stuck: clientLooksStuck(progress, stage, msInStarted),
        timedOut: poll.attemptTimedOut && progress !== null,
        isPending: prepare.isPending,
        busyAction: prepare.isPending ? launchedBy : null,
        error: isAlreadyVerified(prepare.error) ? null : prepare.error,
        pendingSwitch,
        launch,
        confirmSwitch,
        cancelSwitch: () => {
            setPendingSwitch(null);
        },
        revealQr: () => {
            if (progress) setRevealedFor(progress.attemptId);
        },
        reopenSdk,
        closeSdk: () => {
            setSdk(null);
        },
        checkNow: async () => {
            poll.restartAttemptClock();
            await poll.refetch();
        },
    };
}
